import type { Entitlement } from "./entitlements";
import { TVD_FULL, TVM_FULL } from "./entitlements";

export type AppId = "tvm" | "tvd";

export type AppAccessModel = "free" | "license";

/** Deep-link config for a Capacitor build. Web-only apps leave this null. */
export interface NativeScheme {
  /** URL scheme registered in the native manifest, with trailing "://". */
  scheme: string;
  /** SSO audience a callback on this scheme resolves to. Native builds have no
   *  hostname of their own, so they borrow the app's canonical web host. */
  audience: string;
}

export interface AppDefinition {
  appId: AppId;
  /** Product name as it appears in copy. Never the acronym (naming rule). */
  name: string;
  /** Entitlement that unlocks full access. Plans grant these; apps check them. */
  entitlement: Entitlement;
  /** Where /home sends the browser when launching the app. */
  launchHost: string;
  /** Every host accepted as an SSO audience for this app. Lowercase. */
  ssoHosts: readonly string[];
  nativeScheme: NativeScheme | null;
  /** False until the app is actually deployed: /home renders a "coming soon"
   *  tile and mintRedirectToken refuses to issue tokens for it. */
  available: boolean;
  /**
   * Who may open it. `free`: any verified user, and its entitlement is in every
   * user's claims. `license`: only users holding a current license, granted app
   * by app from the admin panel (see /appGrants).
   *
   * Licensed apps are always shown on /home — locked, with a "request license"
   * button — and mintRedirectToken refuses tokens to anyone without the
   * entitlement, so knowing the app's URL is worth nothing.
   */
  access: AppAccessModel;
}

export const APPS: readonly AppDefinition[] = [
  {
    appId: "tvm",
    name: "Trawl Vision Mobile",
    entitlement: TVM_FULL,
    launchHost: "acruxsoft-tvm.web.app",
    ssoHosts: ["tvm.acruxsoft.net", "acruxsoft-tvm.web.app"],
    nativeScheme: { scheme: "acruxsoft-tvm://", audience: "tvm.acruxsoft.net" },
    available: true,
    // Libre desde la Pieza #8 hasta el 2026-10-24, cuando se aclaró con Frank
    // que TVM también va por licencia, igual que el TVD.
    access: "license",
  },
  {
    appId: "tvd",
    name: "Trawl Vision Designer",
    entitlement: TVD_FULL,
    launchHost: "acruxsoft-tvd.web.app",
    ssoHosts: ["tvd.acruxsoft.net", "acruxsoft-tvd.web.app"],
    nativeScheme: null,
    available: true,
    access: "license",
  },
] as const;

/**
 * Whether this user is allowed to hold a session in this app at all — the one
 * rule the SSO minter and the app grid both answer to, so a licensed app can't
 * end up locked in the UI but reachable through a hand-made token.
 */
export function canEnterApp(app: AppDefinition, entitlements: readonly string[]): boolean {
  if (!app.available) return false;
  if (app.access === "free") return true;
  return entitlements.includes(app.entitlement);
}

/** Entitlements every user holds, because their apps are free. */
export const FREE_APP_ENTITLEMENTS: readonly Entitlement[] = APPS.filter(
  (a) => a.access === "free"
).map((a) => a.entitlement);

/** Apps that need a license — the ones the admin grants and users request. */
export function licensedApps(): AppDefinition[] {
  return APPS.filter((a) => a.access === "license");
}

/** Accepted as an SSO audience for any app — local development only. */
export const DEV_SSO_HOSTS: readonly string[] = ["localhost"];

export function appById(appId: string): AppDefinition | null {
  return APPS.find((a) => a.appId === appId) ?? null;
}

/** Every host mintRedirectToken will issue a token for, including dev hosts. */
export function ssoAudienceAllowlist(): string[] {
  return [...APPS.flatMap((a) => [...a.ssoHosts]), ...DEV_SSO_HOSTS];
}

/** The app an SSO audience belongs to, or null for dev/unknown hosts. */
export function appForAudience(audience: string): AppDefinition | null {
  const host = audience.toLowerCase();
  return APPS.find((a) => a.ssoHosts.includes(host)) ?? null;
}

/**
 * Canonical SSO audience for a post-login return URL, or null when the URL
 * points somewhere we won't hand a custom token to. Native custom schemes map
 * to their app's declared audience; everything else matches on hostname.
 */
export function audienceForReturnUrl(returnUrl: string): string | null {
  for (const app of APPS) {
    if (app.nativeScheme && returnUrl.startsWith(app.nativeScheme.scheme)) {
      return app.nativeScheme.audience;
    }
  }
  try {
    const host = new URL(returnUrl).hostname.toLowerCase();
    return ssoAudienceAllowlist().includes(host) ? host : null;
  } catch {
    return null;
  }
}
